import React, { useState } from "react";
import { InputWrapper, Select, SelectLayout } from "@amzn/stencil-react-components/form";
import { Col, Row } from "@amzn/stencil-react-components/layout";
import { Label, Text } from "@amzn/stencil-react-components/text";
import { FormInputItem, DocUploadSelectOptions } from "../../utility/types/common";
import { useChecklistArb } from "../../hooks/use-arb";
import { DOC_UPLOAD_FORM_INPUT_IDS } from "../../utility/enums/common";

interface FormInputSelectProps {
  parentCategory?: string;
  defaultValue?: string;
  inputItem: FormInputItem;
  isSearchable?: boolean;
  onChange?: Function;
}

const FormInputSelect = ({ defaultValue, inputItem, onChange, isSearchable, parentCategory }: FormInputSelectProps) => {
  const {
    id,
    label,
    required,
    tooltip,
    tooltipTranslationKey,
    dataKey,
    errorMessage,
    hasError,
    name,
    selectOptions,
    placeholder,
    placeholderTranslationKey,
    errorMessageTranslationKey,
    labelTranslationKey,
    layout = SelectLayout.Default,
  } = inputItem;

  const [value, setValue] = useState(defaultValue || "");

  const { getMessage } = useChecklistArb();

  const labelText = label && labelTranslationKey ? getMessage(labelTranslationKey, label) : label;
  const errorText =
    errorMessageTranslationKey && errorMessage
      ? getMessage(errorMessageTranslationKey, errorMessage)
      : errorMessage || undefined;
  const placeholderText =
    placeholder && placeholderTranslationKey ? getMessage(placeholderTranslationKey, placeholder) : placeholder;
  const tooltipText = tooltip && tooltipTranslationKey ? getMessage(tooltipTranslationKey, tooltip) : tooltip;

  const renderOptions = (selectOptions: DocUploadSelectOptions[] | undefined) => {
    if (selectOptions) {
      if (parentCategory && id === DOC_UPLOAD_FORM_INPUT_IDS.SELECT_MULTIPLE_DROPDOWN_CHILDREN) {
        return selectOptions
          .filter((option) => option.documentParentCategory === parentCategory)
          .map((option) => {
            if (option.noAction) {
              return (
                <Text
                  key={option.value}
                  className="non-actionable"
                  fontSize={"T200"}
                  fontWeight={"bold"}
                  aria-disabled={true}
                >
                  {option.displayValue}
                </Text>
              );
            }

            return option.displayValue;
          });
      }

      return selectOptions.map((option) => {
        if (option.noAction) {
          return (
            <Text
              key={option.value}
              className="non-actionable"
              fontSize={"T200"}
              fontWeight={"bold"}
              aria-disabled={true}
            >
              {option.displayValue}
            </Text>
          );
        }

        return option.displayValue;
      });
    }
    return [];
  };

  const handleSelectChange = (e: string | React.ReactNode) => {
    if (typeof e !== "string") return;

    setValue(e);
    const selection = selectOptions?.filter((option: DocUploadSelectOptions) => option.displayValue === e)[0];
    onChange && onChange(selection);
  };

  return (
    <Col className="formInputItem">
      <InputWrapper
        id={id}
        required={!!required}
        error={!!hasError}
        tooltipText={tooltipText}
        footer={hasError ? errorText : undefined}
        labelText={labelText}
        renderLabel={() => (
          <Label htmlFor={id}>
            <Row
              style={{ alignItems: "center", width: "100%" }}
              id={`${id}-renderLabel`}
              gridGap={"1rem"}
              data-testid={`${id}-renderLabel`}
            >
              <Row gridGap={8} justifyContent={required ? "flex-start" : "space-between"} width="100%">
                <Text fontWeight={"regular"}>{labelText}</Text>
                <Row>
                  {required ? (
                    <Text color="red"> * </Text>
                  ) : (
                    <Text>{getMessage("Checklist-Common-Optional", "Optional")}</Text>
                  )}
                </Row>
              </Row>
            </Row>
          </Label>
        )}
      >
        {(inputProps) => (
          <Select
            {...inputProps}
            aria-label={name}
            aria-required={required}
            aria-invalid={hasError}
            width="100%"
            name={name}
            id={id}
            data-testid={id}
            key={`${id}-${dataKey}`}
            defaultValue={defaultValue}
            aria-describedby={`introduction ${inputProps["aria-describedby"]}`}
            onChange={handleSelectChange}
            options={renderOptions(selectOptions)}
            placeholder={placeholderText || ""}
            layout={layout}
            isSearchable={isSearchable}
            value={value}
          />
        )}
      </InputWrapper>
    </Col>
  );
};

export default FormInputSelect;
